// src/components/admin/flows/FlowAutomation.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    LayoutDashboard, GitBranch, Link2, Zap, BookTemplate, History
} from 'lucide-react';
import { FlowAutomationProvider } from '../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import FlowDashboard from './FlowDashboard';
import WorkflowList from './WorkflowList';
import WorkflowBuilder from './WorkflowBuilder';
import ApprovalChainBuilder from './ApprovalChainBuilder';
import AutomationRulesList from './AutomationRulesList';
import TemplateLibrary from './TemplateLibrary';
import ExecutionHistory from './ExecutionHistory';
import ExecutionDetail from './ExecutionDetail';

const TABS = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'workflows', label: 'Workflows', icon: GitBranch },
    { key: 'approvals', label: 'Approval Chains', icon: Link2 },
    { key: 'automations', label: 'Automations', icon: Zap },
    { key: 'templates', label: 'Templates', icon: BookTemplate },
    { key: 'history', label: 'History', icon: History },
];

// Wrapper for standalone execution detail route
const ExecutionDetailRoute = () => {
    const { executionId } = useParams();
    const navigate = useNavigate();
    return (
        <ExecutionDetail
            executionId={executionId}
            onBack={() => navigate('/admin/flows?tab=history')}
        />
    );
};

const FlowAutomation = () => {
    const { theme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    const [activeTab, setActiveTab] = useState('dashboard');

    // Sync tab from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab && TABS.some(t => t.key === tab)) {
            setActiveTab(tab);
        }
    }, [location.search]);

    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        navigate(`/admin/flows?tab=${tabKey}`, { replace: true });
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return <FlowDashboard onNavigate={handleTabChange} />;
            case 'workflows':
                return <WorkflowList />;
            case 'approvals':
                return <ApprovalChainBuilder />;
            case 'automations':
                return <AutomationRulesList />;
            case 'templates':
                return <TemplateLibrary />;
            case 'history':
                return <ExecutionHistory />;
            default:
                return <FlowDashboard onNavigate={handleTabChange} />;
        }
    };

    return (
        <FlowAutomationProvider>
            <Routes>
                <Route path="/builder/:workflowId" element={<WorkflowBuilder />} />
                <Route path="/builder" element={<WorkflowBuilder />} />
                <Route path="/executions/:executionId" element={<ExecutionDetailRoute />} />
                <Route
                    path="*"
                    element={
                        <div className="space-y-6">
                            {/* Header */}
                            <div>
                                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Flow Automation
                                </h1>
                                <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Build workflows, approval chains, and task automations
                                </p>
                            </div>

                            {/* Tab Navigation */}
                            <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <nav className="tour-flow-tabs -mb-px flex space-x-6 overflow-x-auto" aria-label="Tabs">
                                    {TABS.map((tab) => {
                                        const Icon = tab.icon;
                                        const isActive = activeTab === tab.key;
                                        return (
                                            <button
                                                key={tab.key}
                                                onClick={() => handleTabChange(tab.key)}
                                                className={`flex items-center gap-2 whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                                                    isActive
                                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                                        : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                                                }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                {tab.label}
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>

                            {/* Tab Content */}
                            {renderTabContent()}
                        </div>
                    }
                />
            </Routes>
        </FlowAutomationProvider>
    );
};

export default FlowAutomation;
